import { useEffect, useRef, type ReactNode } from 'react';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * Hides the heading visually while keeping it for assistive tech. For dialogs
   * whose content already carries the title — a garment preview captions the
   * image with the name, and printing it again above is duplication a sighted
   * user reads as a mistake.
   */
  titleHidden?: boolean;
}

/**
 * Hand-rolled dialog. The only primitive here that genuinely needs behaviour
 * rather than styling: focus has to be trapped, Escape has to close, and the
 * page behind must not scroll.
 *
 * `position: fixed` plus `overscroll-behavior: contain` on the panel is what
 * stops a scroll gesture inside the dialog chaining through to the page beneath
 * on iOS, which otherwise scrolls the background and leaves the dialog floating
 * over the wrong content.
 */
export function Modal({ open, onClose, title, children, footer, titleHidden }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const focusables = (): HTMLElement[] =>
      Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((el) => !el.hasAttribute('disabled'));

    focusables()[0]?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const items = focusables();
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
      previouslyFocused.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      style={{ background: 'rgb(16 24 40 / 0.5)' }}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-full max-w-lg rounded-t-xl bg-surface p-6 pb-safe-action shadow-overlay sm:rounded-xl sm:pb-6"
        style={{ overscrollBehavior: 'contain', maxHeight: '90dvh', overflowY: 'auto' }}
      >
        <h2 className={titleHidden ? 'sr-only' : 'text-xl font-semibold text-primary'}>{title}</h2>
        <div className={titleHidden ? '' : 'mt-4'}>{children}</div>
        {footer && <div className="mt-6 flex justify-end gap-3">{footer}</div>}
      </div>
    </div>
  );
}
